import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  Login, Home, Profile,
  FinanceStatutory,
  ManageTMPS,
  NPAT,
  CompanyInformation,
  OEReportsChart,
  Ownership,
  OwnershipDataSheet,
  OwnershipScorecard,
  OwnershipDocumentation,
  OwnershipTemplates,
  ManagementControl,
  ManagementControlScorecardPage,
  Yes4Youth,
  SkillsDevelopment,
  SkillsDevelopmentScorecard,
  SkillsDevelopmentDocumentation,
  SDSubmitDataSheet,
  PreferentialProcurement,
  PreferentialProcurementScorecard,
  PPSubmitDataSheetNew,
  SupplierDevelopment,
  EnterpriseSupplierDevelopment,
  ESDScorecard,
  ESDSubmitDataSheet,
  SocioEconomicDevelopment,
  SEDScorecard,
  SEDSubmitDataSheet,
  Reports,
  AllReportsPage,
  ScenarioPlanning,
  MCBoardRepresentationExco,
  MCDocumentationUpload,
  MCOther,
  ManagementControlSubmitDataSheetPage,
  EmploymentEquity,
  MCReportsChart,
  SDReportsChart,
  PPReportsChart,
  ESDReportsChart,
  SEDReportsChart,
  AddUser,
  TemplateUpload,
  AllDataSheets,
  FinancialYearSelect,
  SelectContext,
  DocumentationPage,
} from './pages';
import { Navbar } from './components';
import { authService } from './services';
import { isAuthenticated } from './utils/auth';
import { EntityConfigProvider } from './utils/EntityConfigContext';
import { RoleProvider, useRole } from './utils/RoleContext';
import { RoleGuard } from './components';
import { isClientRole } from './utils/roles';
import ClientApp from './client/ClientApp';
import './styles/App.scss';

const PrivateRoute = ({ children }) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" />;
};

const ContextRoute = ({ children }) => {
  if (!isAuthenticated()) return <Navigate to="/login" />;
  if (!localStorage.getItem('company_id')) return <Navigate to="/select-context" />;
  return <>{children}</>;
};

function AppContent() {
  const { role, setRole } = useRole();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      if (isAuthenticated()) {
        try {
          const userData = await authService.getCurrentUser();
          setUser({ username: userData.username, isStaff: !!userData.is_staff });
          setRole(userData.role ?? null);
        } catch (error) {
          console.error('Failed to load user:', error);
        }
      }
      setIsLoading(false);
    };
    loadUser();
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    window.location.href = '/login';
  };

  if (isLoading) {
    return <div className="app-loading">Loading...</div>;
  }

  // Client roles (BEE Manager, HR, Logistics) get the simple one-screen dashboard
  // instead of the dense consultant app.
  if (isAuthenticated() && isClientRole(role)) {
    return <ClientApp username={user?.username} onLogout={handleLogout} />;
  }

  return (
    <Router>
      <div className="app">
        <Navbar
          isAuthenticated={!!user}
          userName={user?.username}
          isStaff={!!user?.isStaff}
          onLogout={handleLogout}
        />
        <main className="app-main">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/select-context" element={<PrivateRoute><SelectContext /></PrivateRoute>} />
            <Route path="/" element={<ContextRoute><Home /></ContextRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />

            {/* Finance & Statutory — Consultant, BEE Manager, Finance */}
            <Route path="/finance-statutory" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance']}><FinanceStatutory /></RoleGuard></ContextRoute>} />
            <Route path="/finance-statutory/company-information" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance']}><CompanyInformation /></RoleGuard></ContextRoute>} />
            <Route path="/finance-statutory/manage-tmps" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance']}><ManageTMPS /></RoleGuard></ContextRoute>} />
            <Route path="/finance-statutory/npat" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance']}><NPAT /></RoleGuard></ContextRoute>} />

            {/* Ownership — Consultant, BEE Manager */}
            <Route path="/ownership" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><Ownership /></RoleGuard></ContextRoute>} />
            <Route path="/ownership/data-sheet" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><OwnershipDataSheet /></RoleGuard></ContextRoute>} />
            <Route path="/ownership/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><OwnershipScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/ownership/reports" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><OEReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/ownership/templates-resources" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><OwnershipTemplates /></RoleGuard></ContextRoute>} />

            {/* Ownership - single key */}
            <Route path="/ownership/documentation" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><DocumentationPage elementKey="ownership" /></RoleGuard></ContextRoute>} />


            {/* Management Control — Consultant, BEE Manager, HR */}
            <Route path="/management/management-control" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><ManagementControl /></RoleGuard></ContextRoute>} />
            <Route path="/management/management-control/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><ManagementControl /></RoleGuard></ContextRoute>} />
            <Route path="/management/yes4youth" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><Yes4Youth /></RoleGuard></ContextRoute>} />
            <Route path="/management/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><ManagementControlScorecardPage /></RoleGuard></ContextRoute>} />
            <Route path="/management/scorecard/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><ManagementControlScorecardPage /></RoleGuard></ContextRoute>} />
            <Route path="/management/board-representation" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><MCBoardRepresentationExco /></RoleGuard></ContextRoute>} />
            <Route path="/management/other" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><MCOther /></RoleGuard></ContextRoute>} />
            <Route path="/management/submit-datasheet" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><ManagementControlSubmitDataSheetPage /></RoleGuard></ContextRoute>} />
            <Route path="/management/submit-datasheet/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><ManagementControlSubmitDataSheetPage /></RoleGuard></ContextRoute>} />

            {/* Management Control family: management_control, management_control_transport, employment_equity */}
            <Route path="/management/documentation/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><DocumentationPage /></RoleGuard></ContextRoute>} />

            {/* Employment Equity — Consultant, BEE Manager, HR */}
            <Route path="/management/employment-equity" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><EmploymentEquity /></RoleGuard></ContextRoute>} />

            {/* Skills Development — Consultant, BEE Manager, HR */}
            <Route path="/skills-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><SkillsDevelopment /></RoleGuard></ContextRoute>} />
            <Route path="/skills-development/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><SkillsDevelopment /></RoleGuard></ContextRoute>} />
            <Route path="/skills-development/submit-datasheet" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><SDSubmitDataSheet /></RoleGuard></ContextRoute>} />
            <Route path="/skills-development/submit-datasheet/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><SDSubmitDataSheet /></RoleGuard></ContextRoute>} />
            <Route path="/skills-development/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><SkillsDevelopmentScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/skills-development/scorecard/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><SkillsDevelopmentScorecard /></RoleGuard></ContextRoute>} />

            {/* Skills Development family: skills_development, skills_development_transport */}
            <Route path="/skills-development/documentation/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'HR']}><DocumentationPage /></RoleGuard></ContextRoute>} />

            {/* ESD / Procurement — Consultant, BEE Manager, Logistics */}
            <Route path="/esd/preferential-procurement" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><PreferentialProcurement /></RoleGuard></ContextRoute>} />
            <Route path="/esd/preferential-procurement/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><PreferentialProcurementScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/esd/preferential-procurement/submit-datasheet" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><PPSubmitDataSheetNew /></RoleGuard></ContextRoute>} />
            <Route path="/esd/enterprise-supplier-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><EnterpriseSupplierDevelopment /></RoleGuard></ContextRoute>} />
            <Route path="/esd/enterprise-supplier-development/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><EnterpriseSupplierDevelopment /></RoleGuard></ContextRoute>} />
            <Route path="/esd/enterprise-supplier-development/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><ESDScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/esd/enterprise-supplier-development/scorecard/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><ESDScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/esd/enterprise-supplier-development/submit-datasheet" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><ESDSubmitDataSheet /></RoleGuard></ContextRoute>} />
            <Route path="/esd/enterprise-supplier-development/submit-datasheet/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><ESDSubmitDataSheet /></RoleGuard></ContextRoute>} />
            <Route path="/esd/supplier-development" element={<Navigate to="/esd/enterprise-supplier-development" replace />} />
            <Route path="/esd/supplier-development/scorecard" element={<Navigate to="/esd/enterprise-supplier-development/scorecard" replace />} />

            {/* ESD/Procurement family: procurement, enterprise_supplier_development, enterprise_development, supplier_development, enterprise_development_transport */}
            <Route path="/esd/documentation/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Logistics']}><DocumentationPage /></RoleGuard></ContextRoute>} />

            {/* Socio-Economic Development — Consultant, BEE Manager */}
            <Route path="/socio-economic-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><SocioEconomicDevelopment /></RoleGuard></ContextRoute>} />
            <Route path="/socio-economic-development/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><SocioEconomicDevelopment /></RoleGuard></ContextRoute>} />
            <Route path="/sed/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><SEDScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/sed/scorecard/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><SEDScorecard /></RoleGuard></ContextRoute>} />
            <Route path="/sed/submit-datasheet" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><SEDSubmitDataSheet /></RoleGuard></ContextRoute>} />
            <Route path="/sed/submit-datasheet/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><SEDSubmitDataSheet /></RoleGuard></ContextRoute>} />

            {/* SED family: sed, sed_transport */}
            <Route path="/socio-economic-development/documentation/:elementKey" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><DocumentationPage /></RoleGuard></ContextRoute>} />

            {/* Reports — all roles */}
            <Route path="/reports/all" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><AllReportsPage /></RoleGuard></ContextRoute>} />
            <Route path="/reports/scenario" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><ScenarioPlanning /></RoleGuard></ContextRoute>} />
            <Route path="/reports" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><Reports /></RoleGuard></ContextRoute>} />
            <Route path="/reports/scorecard" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><MCReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/reports/ownership" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><OEReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/reports/skills-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><SDReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/reports/preferential-procurement" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><PPReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/reports/enterprise-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><ESDReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/reports/supplier-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><ESDReportsChart /></RoleGuard></ContextRoute>} />
            <Route path="/reports/socio-economic-development" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics']}><SEDReportsChart /></RoleGuard></ContextRoute>} />

            {/* Data Sheet Templates: staff (admin) accounts only; the API enforces this too */}
            <Route path="/admin/templates" element={<PrivateRoute>{user?.isStaff ? <TemplateUpload /> : <div style={{ padding: '2rem', textAlign: 'center' }}><h2>Access Denied</h2><p>You do not have permission to view this page.</p><a href="/">Go to Home</a></div>}</PrivateRoute>} />

            {/* Add User — Consultant only */}
            <Route path="/add-user" element={<ContextRoute><RoleGuard allowedRoles={['Consultant']}><AddUser /></RoleGuard></ContextRoute>} />

            {/* Financial Year — Consultant only */}
            <Route path="/financial-year/select" element={<ContextRoute><RoleGuard allowedRoles={['Consultant']}><FinancialYearSelect /></RoleGuard></ContextRoute>} />

            {/* Finance & Statutory — single key */}
            <Route path="/finance-statutory/documentation" element={<ContextRoute><RoleGuard allowedRoles={['Consultant','BEE Manager','Finance']}><DocumentationPage elementKey="finance_statutory" /></RoleGuard></ContextRoute>} />

            {/* All Data Sheets — Consultant, BEE Manager */}
            <Route path="/all-data-sheets" element={<ContextRoute><RoleGuard allowedRoles={['Consultant', 'BEE Manager']}><AllDataSheets /></RoleGuard></ContextRoute>} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function App() {
  return (
    <RoleProvider>
      <EntityConfigProvider>
        <AppContent />
      </EntityConfigProvider>
    </RoleProvider>
  );
}

export default App;
